import { useHoverOpen, type UseHoverOpenHandlers } from "@coval/ui"

interface FilterSectionHoverHandlers extends UseHoverOpenHandlers {
  onPointerDown: () => void
}

interface UseFilterSectionHoverOptions {
  isOpen: boolean
  onToggle: () => void
  disabled?: boolean
}

/**
 * Hover-to-open for a filter menu's section rows, in the toggle-shaped API the
 * sections are wired with: the menu owns which row is expanded and only knows
 * how to flip it, so an open request on a row that is already open would close
 * it. `useHoverOpen` never asks to open what is open, which is what makes the
 * translation to a bare `onToggle()` safe.
 */
export function useFilterSectionHover({
  isOpen,
  onToggle,
  disabled = false,
}: UseFilterSectionHoverOptions): FilterSectionHoverHandlers {
  const hoverHandlers = useHoverOpen({
    open: isOpen,
    onOpenChange: (open) => {
      if (open) {
        onToggle()
      }
    },
    disabled,
  })

  return {
    ...hoverHandlers,
    // A click is about to become the source of truth for this interaction.
    // Cancel the pending hover-open first so the delayed callback cannot land
    // immediately afterward and toggle the same section closed again.
    onPointerDown: hoverHandlers.onPointerLeave,
  }
}
